import { NetworkConfig } from '../signing/types';

const NETWORK_LIST_KEY = 'networkList';

function getNetworkKey(networkId: string): string {
  return `network:${networkId}`;
}

function getKeystoreKey(networkId: string, accountId: string): string {
  return `keystore:${networkId}:${accountId}`;
}

function getNetworkList(): string[] {
  const raw = localStorage.getItem(NETWORK_LIST_KEY);
  if (!raw) return [];
  try {
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

function setNetworkList(list: string[]): void {
  localStorage.setItem(NETWORK_LIST_KEY, JSON.stringify(list));
}

export function saveConfig(config: NetworkConfig & { networkId: string }): void {
  const key = getNetworkKey(config.networkId);
  localStorage.setItem(key, JSON.stringify(config));

  const list = getNetworkList();
  if (!list.includes(config.networkId)) {
    list.push(config.networkId);
    setNetworkList(list);
  }
}

/**
 * Returns a human-readable display name for a network.
 * Public networks: "Testnet", "Mainnet", "Previewnet"
 * Custom networks: the config's `name` field, or "Custom" as fallback
 */
export function getNetworkDisplayName(networkId: string): string {
  if (!networkId) return '';
  if (networkId === 'testnet') return 'Testnet';
  if (networkId === 'mainnet') return 'Mainnet';
  if (networkId === 'previewnet') return 'Previewnet';
  if (networkId.startsWith('custom-')) {
    const config = getConfig(networkId);
    return config?.name || 'Custom';
  }
  return networkId;
}

export function getConfig(networkId: string): NetworkConfig | null {
  const raw = localStorage.getItem(getNetworkKey(networkId));
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function getAllConfigs(): (NetworkConfig & { networkId: string })[] {
  const list = getNetworkList();
  const configs: (NetworkConfig & { networkId: string })[] = [];

  for (const networkId of list) {
    const raw = localStorage.getItem(getNetworkKey(networkId));
    if (raw) {
      try {
        configs.push(JSON.parse(raw));
      } catch {
        // skip corrupt entries
      }
    }
  }

  return configs;
}

export function deleteConfig(networkId: string): void {
  localStorage.removeItem(getNetworkKey(networkId));
  const list = getNetworkList().filter(id => id !== networkId);
  setNetworkList(list);
}

export function getKeystore(networkId: string, accountId: string): string | null {
  return localStorage.getItem(getKeystoreKey(networkId, accountId));
}

export function saveKeystore(networkId: string, accountId: string, base64Data: string): void {
  localStorage.setItem(getKeystoreKey(networkId, accountId), base64Data);
}

export function deleteKeystore(networkId: string, accountId: string): void {
  localStorage.removeItem(getKeystoreKey(networkId, accountId));
}

export async function generateNetworkId(nodes: Record<string, string>): Promise<string> {
  const sortedKeys = Object.keys(nodes).sort();
  const joined = sortedKeys.join(',');
  const encoder = new TextEncoder();
  const data = encoder.encode(joined);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return `custom-${hashHex.substring(0, 12)}`;
}

export interface KeystoreMeta {
  accountId: string;
  publicKey: string;
  keyType: 'ED25519' | 'ECDSA';
  createdAt: number;
  updatedAt: number;
}

function getKeystoreMetaKey(networkId: string, accountId: string): string {
  return `keystoreMeta:${networkId}:${accountId}`;
}

export function saveKeystoreMeta(networkId: string, accountId: string, meta: KeystoreMeta): void {
  localStorage.setItem(getKeystoreMetaKey(networkId, accountId), JSON.stringify(meta));
}

export function getKeystoreMeta(networkId: string, accountId: string): KeystoreMeta | null {
  const raw = localStorage.getItem(getKeystoreMetaKey(networkId, accountId));
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function deleteKeystoreMeta(networkId: string, accountId: string): void {
  localStorage.removeItem(getKeystoreMetaKey(networkId, accountId));
}

export function getKeystoreMetaForNetwork(networkId: string): KeystoreMeta | null {
  const all = getAllKeystoreMetaForNetwork(networkId);
  return all.length > 0 ? all[0] : null;
}

export function getAllKeystoreMetaForNetwork(networkId: string): KeystoreMeta[] {
  const results: KeystoreMeta[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(`keystoreMeta:${networkId}:`)) {
      const raw = localStorage.getItem(key);
      if (raw) {
        try {
          results.push(JSON.parse(raw));
        } catch {
          continue;
        }
      }
    }
  }
  // Sort by most recently updated first
  return results.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

export function clearSessionKeys(): void {
  const sessionKeys = ['token', 'accountId', 'connectedNetwork', 'suppressNetworkSwitchWarning'];
  for (const key of sessionKeys) {
    localStorage.removeItem(key);
  }
}
